import { useEffect, useState } from "react";
import { getSettings, saveSettings, testConnection, sendTest } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Plugs, PaperPlaneTilt, FloppyDisk, Eye, EyeSlash } from "@phosphor-icons/react";

const inputCls =
  "w-full rounded-none border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB] transition-colors duration-150";
const labelCls =
  "block text-[11px] tracking-[0.15em] uppercase text-[#52525B] font-semibold mb-2";

const PRESETS = {
  gmail: { host: "smtp.gmail.com", port: 587, encryption: "tls" },
  outlook: { host: "smtp-mail.outlook.com", port: 587, encryption: "tls" },
  ssl465: { host: "", port: 465, encryption: "ssl" },
};

export default function Settings() {
  const [form, setForm] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    encryption: "tls",
    default_from_name: "",
    default_from_email: "",
    default_reply_to: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    getSettings().then((s) => setForm((f) => ({ ...f, ...s }))).catch(() => {});
  }, []);

  const set = (k) => (e) => {
    const v = k === "port" ? parseInt(e.target.value || "0", 10) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings(form);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await saveSettings(form);
      const res = await testConnection();
      toast.success(res.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally {
      setTesting(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) return toast.error("Enter a test recipient email");
    try {
      await saveSettings(form);
      const res = await sendTest(testEmail);
      toast.success(res.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    }
  };

  const applyPreset = (p) => setForm({ ...form, ...PRESETS[p] });

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Configuration"
        title="SMTP Settings"
        subtitle="Connect your own SMTP server. Credentials are stored to send on your behalf."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 border-t border-[#E5E7EB]">
        <div className="lg:col-span-2 p-10 border-r border-[#E5E7EB]">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#52525B]">
              Quick presets
            </span>
          </div>
          <div className="flex gap-2 mb-8">
            {[
              ["gmail", "Gmail"],
              ["outlook", "Outlook"],
              ["ssl465", "SSL 465"],
            ].map(([key, label]) => (
              <button
                key={key}
                data-testid={`preset-${key}`}
                onClick={() => applyPreset(key)}
                className="rounded-none border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-semibold hover:border-[#09090B] transition-colors duration-150"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            <div className="md:col-span-2">
              <label className={labelCls}>SMTP host</label>
              <input data-testid="smtp-host-input" className={`${inputCls} font-mono`} placeholder="smtp.yourhost.com" value={form.host} onChange={set("host")} />
            </div>
            <div>
              <label className={labelCls}>Port</label>
              <input data-testid="smtp-port-input" type="number" className={`${inputCls} font-mono`} value={form.port} onChange={set("port")} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <label className={labelCls}>Username</label>
              <input data-testid="smtp-username-input" className={`${inputCls} font-mono`} placeholder="you@yourhost.com" value={form.username} onChange={set("username")} />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <div className="relative">
                <input
                  data-testid="smtp-password-input"
                  type={showPass ? "text" : "password"}
                  className={`${inputCls} font-mono pr-10`}
                  value={form.password}
                  onChange={set("password")}
                />
                <button
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#09090B]"
                >
                  {showPass ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <label className={labelCls}>Encryption</label>
            <div className="flex gap-2">
              {[
                ["tls", "STARTTLS (587)"],
                ["ssl", "SSL/TLS (465)"],
                ["none", "None"],
              ].map(([val, label]) => (
                <button
                  key={val}
                  data-testid={`encryption-${val}`}
                  onClick={() => setForm({ ...form, encryption: val })}
                  className={`rounded-none border px-4 py-2 text-xs font-semibold transition-colors duration-150 ${
                    form.encryption === val
                      ? "border-[#2563EB] bg-[#2563EB] text-white"
                      : "border-[#E5E7EB] bg-white hover:border-[#09090B]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-[#E5E7EB] pt-8">
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#52525B] mb-5">
              Default sender identity
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className={labelCls}>Default From name</label>
                <input data-testid="default-from-name-input" className={inputCls} placeholder="Acme Support" value={form.default_from_name} onChange={set("default_from_name")} />
              </div>
              <div>
                <label className={labelCls}>Default From email</label>
                <input data-testid="default-from-email-input" className={`${inputCls} font-mono`} placeholder="noreply@yourdomain.com" value={form.default_from_email} onChange={set("default_from_email")} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Default Reply-To</label>
              <input data-testid="default-reply-to-input" className={`${inputCls} font-mono`} placeholder="me@mypersonal.com" value={form.default_reply_to} onChange={set("default_reply_to")} />
            </div>
          </div>

          <div className="flex gap-3 mt-10">
            <button
              data-testid="save-settings-btn"
              onClick={handleSave}
              disabled={saving}
              className="rounded-none bg-[#09090B] text-white px-6 py-3 text-sm font-semibold flex items-center gap-2 hover:bg-[#2563EB] transition-colors duration-150 disabled:opacity-50"
            >
              <FloppyDisk size={16} weight="fill" /> {saving ? "Saving..." : "Save settings"}
            </button>
            <button
              data-testid="test-connection-btn"
              onClick={handleTest}
              disabled={testing}
              className="rounded-none border border-[#E5E7EB] bg-white px-6 py-3 text-sm font-semibold flex items-center gap-2 hover:border-[#09090B] transition-colors duration-150 disabled:opacity-50"
            >
              <Plugs size={16} weight="fill" /> {testing ? "Testing..." : "Test connection"}
            </button>
          </div>
        </div>

        <div className="p-10 bg-[#F9FAFB]">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#52525B] font-semibold mb-4">
            Send a test email
          </div>
          <div className="border border-[#E5E7EB] bg-white p-5">
            <label className={labelCls}>Recipient</label>
            <input
              data-testid="test-email-input"
              className={`${inputCls} font-mono mb-4`}
              placeholder="you@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
            <button
              data-testid="send-test-btn"
              onClick={handleSendTest}
              className="w-full rounded-none bg-[#10B981] text-white px-6 py-3 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#059669] transition-colors duration-150"
            >
              <PaperPlaneTilt size={16} weight="fill" /> Send test
            </button>
          </div>
          <div className="mt-6 text-xs text-[#52525B] leading-relaxed">
            Save your settings, run <b>Test connection</b> to verify credentials, then send a test email
            to confirm delivery lands in the inbox.
          </div>
        </div>
      </div>
    </div>
  );
}
